import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'

import {
  getPathWithoutI18nPrefix,
  matchI18nPath,
  matchI18nPathList,
} from 'features/i18n/router'
import { ROUTE_PATH } from 'lib/paths'

import { useCurrentUser, useUsersAliveMutation } from 'store/api/yay/user'
import { selectIsMeaningfulTime } from 'store/selectors'

// meaningful time condition (YAY-14710)
// 1. screen is active
// 2. one of these
//   - post creation - (YAY-15221) - same screen with timelines and cirlces
//   - letter creation (YAY-15222) - same screen with other user's profiles
//   - other user's profiles (YAY-15223)
//   - chat rooms (YAY-15276) - exclude chat list and chat settings - see ChatRoom.tsx
//   - public/following timeline (YAY-15277)
//   - circles (YAY-15278) - exclude circle details and circle settings
//   - posts/conversations (YAY-15279)
//   - calls with more than one person (YAY-15280) - count even it is in background - see StreamList.tsx

const ALIVE_INTERVAL = 30 * 1000 // 30 sec

export const useCountMeaningfulTime = () => {
  const location = useLocation()

  const [isActive, setIsActive] = useState(true)
  const { data: currentUser } = useCurrentUser()
  const isMeaningfulTime = useSelector(selectIsMeaningfulTime)

  const [alive] = useUsersAliveMutation()

  const isMeaningfulPath = useMemo(() => {
    if (!currentUser?.id) return false

    //   - other user's profiles (YAY-15223)
    const isUserProfileRoute = matchI18nPath(ROUTE_PATH.USER(), {
      startsWith: true,
    })
    if (isUserProfileRoute) {
      // can't use useParams because this hook will be call outside of route component
      const path = getPathWithoutI18nPrefix(location.pathname)
      const userId = +path?.split?.('/')?.filter?.((x) => x && !isNaN(+x))?.[0]

      return currentUser.id !== userId
    }

    //   - public/following timeline (YAY-15277)
    const isTimelineRoute = matchI18nPathList([
      ROUTE_PATH.TIMELINE_PUBLIC,
      ROUTE_PATH.TIMELINE_FOLLOWING,
    ])
    if (isTimelineRoute) return true

    //   - circles (YAY-15278) - exclude circle details and circle settings
    const isGroupRoute = matchI18nPath(ROUTE_PATH.GROUP(), { startsWith: true })
    if (isGroupRoute) {
      const isGroupExcludeRoute = matchI18nPathList([
        ROUTE_PATH.GROUP_DETAIL(),
        ROUTE_PATH.GROUP_INFORMATION(),
        // no group setting here because it is a modal
      ])

      return !isGroupExcludeRoute
    }

    //   - posts/conversations (YAY-15279)
    const isPostRoute = matchI18nPathList(
      [ROUTE_PATH.POST(), ROUTE_PATH.CONVERSATION()],
      { startsWith: true }
    )
    if (isPostRoute) return true

    // default
    return false
  }, [location.pathname, currentUser?.id])

  useEffect(() => {
    const onActiveChange: EventListener = (e) => {
      setIsActive(
        e.type === 'focus'
          ? true
          : e.type === 'blur'
          ? false
          : document.visibilityState === 'visible'
      )
    }

    // document visibilitychange event not trigger when user switch the app
    // have to use window focus and blur event instead
    window.addEventListener('focus', onActiveChange)
    window.addEventListener('blur', onActiveChange)

    return () => {
      window.removeEventListener('focus', onActiveChange)
      window.removeEventListener('blur', onActiveChange)
    }
  }, [])

  useEffect(() => {
    if (!currentUser?.id) return
    if (!isMeaningfulPath && !isMeaningfulTime?.state) return
    if (!isActive && !isMeaningfulTime?.force) return

    alive({})

    const interval = setInterval(() => {
      alive({})
    }, ALIVE_INTERVAL)

    return () => {
      clearInterval(interval)
    }
  }, [isActive, isMeaningfulPath, isMeaningfulTime, currentUser?.id])
}
